import { cn } from "@app/lib/utils";
import { useClickOutside } from "@design-system/hooks/use-click-outside";
import { type PortalActions, type ReactAtom } from "@mongez/react-atom";
import { useEffect, useId, useRef } from "react";
import { twMerge } from "tailwind-merge";

export type PortalProps = {
  portal: ReactAtom<any, PortalActions>;
  clickOutside?: boolean;
  esc?: boolean;
  zIndex?: number;
  classes?: {
    root?: string;
    overlay?: string;
    content?: string;
  };
  overlay?: boolean;
  children: React.ReactNode;
};

export function Portal({
  zIndex = 1000,
  portal,
  classes,
  children,
  clickOutside = true,
}: PortalProps) {
  const opened = portal.useOpened();
  const portalRef = useClickOutside(() => portal.close());
  const ref = useRef<HTMLDivElement>(null);

  const id = useId();

  useEffect(() => {
    if (opened && ref.current) {
      ref.current.focus();
    }
  }, [opened]);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [opened]);

  if (!opened) return null;

  return (
    <div
      style={{
        zIndex,
      }}
      className={twMerge(
        "fixed left-0 top-0 h-full w-screen overflow-y-auto",
        classes?.root,
      )}
      id={`portal-${id}`}
      ref={ref as any}>
      <div
        className={cn(
          "absolute inset-0 bg-black bg-opacity-70 backdrop-blur-lg z-10 flex justify-center items-center min-h-screen h-fit p-10",
          classes?.overlay,
        )}>
        <div
          ref={clickOutside ? portalRef : undefined}
          className={cn(
            "bg-white z-20 p-12 animate-fade rounded-2xl w-[1000px] relative overflow-hidden",
            classes?.content,
          )}>
          {children}
        </div>
      </div>
    </div>
  );
}
